import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Input, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  fromMicros,
  getCapabilityCosts,
  getPriceBook,
  getStatementReconciliation,
  importStatement,
  setBillingMode,
  setPrice,
  toMicros,
} from '@/api/meteringParity';
import type {
  BillingMode,
  CapabilityCosts,
  CostLine,
  PriceBook,
  PriceRow,
  ProviderReconciliation,
  StatementReconciliation,
} from '@/api/meteringParity';
import { normalizeApiError } from '@/api/errors';
import { useAuth } from '@/auth/OlAuthContext';
import { InfoTip } from '@/help/InfoTip';
import { formatAbsolute } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;

/** The closed family catalogue (ADR-009), v1 this wave. */
const FAMILIES = [
  'screening',
  'identity-verification',
  'document-analysis',
  'biometric',
  'registry-lookup',
  'risk-scoring',
  'communication',
  'e-sign',
  'video-kyc',
  'banking-verification',
  'forms',
  'transaction-monitoring',
].map((f) => `${f}.v1`);

const thisMonth = () => new Date().toISOString().slice(0, 7);
const validMonth = (m: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(m);
const money = (micros: number | null, currency: string | null) =>
  micros == null || currency == null ? '—' : `${currency} ${fromMicros(micros)}`;
const signed = (n: number) => (n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString());

const RESULT: Record<ProviderReconciliation['result'], { label: string; color?: string }> = {
  MATCHES: { label: 'Matches the meter', color: 'green' },
  DIFFERS: { label: 'Differs from the meter', color: 'gold' },
  NO_STATEMENT: { label: 'No statement imported' },
};

const UNIT: Record<string, string> = { calls: 'Calls', vendorUnits: 'Vendor units', amountMicros: 'Amount' };

// ---- price book ------------------------------------------------------------------------------------

function PriceBookSection({ canWrite }: { canWrite: boolean }) {
  const { message } = App.useApp();
  const [book, setBook] = useState<PriceBook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [family, setFamily] = useState(FAMILIES[0]);
  const [provider, setProvider] = useState('*');
  const [standard, setStandard] = useState('');
  const [standardCurrency, setStandardCurrency] = useState('INR');
  const [vendor, setVendor] = useState('');
  const [vendorCurrency, setVendorCurrency] = useState('INR');

  const load = useCallback(async () => {
    try {
      setBook(await getPriceBook());
      setError(null);
    } catch (e) {
      setError(normalizeApiError(e).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    const standardMicros = toMicros(standard);
    const vendorMicros = vendor.trim() ? toMicros(vendor) : null;
    if (standardMicros == null || (vendor.trim() && vendorMicros == null)) {
      message.error('Costs are decimals of at most six places, for example 12.50');
      return;
    }
    setSaving(true);
    try {
      await setPrice({
        family,
        provider: provider.trim(),
        standardCostMicros: standardMicros,
        standardCurrency: standardCurrency.trim().toUpperCase(),
        ...(vendorMicros != null ? { vendorCostMicros: vendorMicros, vendorCurrency: vendorCurrency.trim().toUpperCase() } : {}),
      });
      message.success(`Priced ${family} / ${provider.trim()}`);
      setStandard('');
      setVendor('');
      await load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (row: PriceRow) => {
    try {
      await setPrice({ family: row.family, provider: row.provider, withdraw: true });
      message.success(`Withdrew the price of ${row.family} / ${row.provider}`);
      await load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    }
  };

  const mode = async (next: BillingMode) => {
    try {
      await setBillingMode(next);
      message.success(`Billing mode is ${next}`);
      await load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    }
  };

  const columns: ColumnsType<PriceRow> = [
    { title: 'Family', dataIndex: 'family', key: 'family', render: (v: string) => <span className="ol-mono">{v}</span> },
    { title: 'Provider', dataIndex: 'provider', key: 'provider', render: (v: string) => <span className="ol-mono">{v}</span> },
    {
      title: 'Standard cost',
      key: 'standard',
      align: 'right',
      render: (_, r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.standardCostMicros, r.standardCurrency)}</span>,
    },
    {
      title: 'Vendor cost',
      key: 'vendor',
      align: 'right',
      render: (_, r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.vendorCostMicros, r.vendorCurrency)}</span>,
    },
    { title: 'Set', key: 'set', render: (_, r) => <Text type="secondary" title={`set by ${r.setBy}`}>{formatAbsolute(r.setAt)}</Text> },
    ...(canWrite
      ? [
          {
            title: '',
            key: 'withdraw',
            width: 110,
            render: (_: unknown, r: PriceRow) => (
              <Popconfirm title={`Withdraw the price of ${r.family} / ${r.provider}?`} onConfirm={() => void withdraw(r)}>
                <Button size="small" data-testid={`price-withdraw-${r.family}-${r.provider}`}>
                  Withdraw
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  const billing = book?.billing;
  return (
    <section style={{ marginBottom: 28 }} data-testid="price-book">
      <Title level={5} style={{ margin: 0 }}>
        Price book
      </Title>
      <Paragraph type="secondary" style={{ margin: '4px 0 12px', fontSize: 13 }}>
        What KYC Hub charges per billed capability call, by family and provider. Each billed call is booked at the price in
        force when it finishes.
      </Paragraph>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Price book request failed" description={error} />}
      {billing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <Text>
            Billing mode <InfoTip surface="admin" field="Billing mode" />
          </Text>
          <Tag color={billing.mode === 'ENFORCED' ? 'blue' : undefined} data-testid="billing-mode">
            {billing.mode}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {billing.mode === 'ENFORCED'
              ? 'Calls with no price, no active contract, no licensed family, or past a ceiling are refused before the vendor is called.'
              : 'Every call runs. Each is booked at its price, or counted as unpriced.'}
            {billing.setAt ? ` Set ${formatAbsolute(billing.setAt)}.` : ''}
          </Text>
          {canWrite && (
            <Popconfirm
              title={billing.mode === 'ENFORCED' ? 'Turn per-hit enforcement off?' : 'Enforce the per-hit entitlement on every call?'}
              onConfirm={() => void mode(billing.mode === 'ENFORCED' ? 'OFF' : 'ENFORCED')}
            >
              <Button size="small" data-testid="billing-mode-toggle">
                {billing.mode === 'ENFORCED' ? 'Turn off' : 'Enforce'}
              </Button>
            </Popconfirm>
          )}
        </div>
      )}
      <Table<PriceRow>
        rowKey="priceId"
        columns={columns}
        dataSource={book?.prices ?? []}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No capability is priced in this hub.' }}
      />
      {canWrite && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }} data-testid="price-form">
          <label style={{ fontSize: 11.5 }}>
            <div>Family</div>
            <Select value={family} onChange={setFamily} options={FAMILIES.map((f) => ({ value: f, label: f }))} style={{ width: 220 }} />
          </label>
          <label style={{ fontSize: 11.5 }}>
            <div>Provider</div>
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} style={{ width: 170 }} data-testid="price-provider" />
          </label>
          <label style={{ fontSize: 11.5 }}>
            <div>
              Standard cost <InfoTip surface="admin" field="Standard cost" />
            </div>
            <Space.Compact>
              <Input value={standardCurrency} onChange={(e) => setStandardCurrency(e.target.value)} style={{ width: 64 }} aria-label="Standard currency" />
              <Input value={standard} onChange={(e) => setStandard(e.target.value)} style={{ width: 110 }} placeholder="12.50" data-testid="price-standard" />
            </Space.Compact>
          </label>
          <label style={{ fontSize: 11.5 }}>
            <div>
              Vendor cost <InfoTip surface="admin" field="Vendor cost" />
            </div>
            <Space.Compact>
              <Input value={vendorCurrency} onChange={(e) => setVendorCurrency(e.target.value)} style={{ width: 64 }} aria-label="Vendor currency" />
              <Input value={vendor} onChange={(e) => setVendor(e.target.value)} style={{ width: 110 }} placeholder="optional" data-testid="price-vendor" />
            </Space.Compact>
          </label>
          <Button type="primary" onClick={() => void save()} loading={saving} disabled={!standard.trim() || !provider.trim()} data-testid="price-save">
            Set price
          </Button>
        </div>
      )}
    </section>
  );
}

// ---- per-hit cost ------------------------------------------------------------------------------------

function CostSection({ month }: { month: string }) {
  const [costs, setCosts] = useState<CapabilityCosts | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!validMonth(month)) return;
    getCapabilityCosts(month)
      .then((c) => {
        setCosts(c);
        setError(null);
      })
      .catch((e) => setError(normalizeApiError(e).message));
  }, [month]);

  const columns: ColumnsType<CostLine> = [
    { title: 'Family', dataIndex: 'family', key: 'family', render: (v: string) => <span className="ol-mono">{v}</span> },
    { title: 'Provider', dataIndex: 'provider', key: 'provider', render: (v: string) => <span className="ol-mono">{v}</span> },
    { title: 'Billed calls', dataIndex: 'billableCalls', key: 'calls', align: 'right' },
    {
      title: 'Unpriced',
      dataIndex: 'unpricedCalls',
      key: 'unpriced',
      align: 'right',
      render: (v: number) => (v > 0 ? <Tag color="gold">{v.toLocaleString()} unpriced</Tag> : '—'),
    },
    {
      title: 'Standard cost',
      key: 'standard',
      align: 'right',
      render: (_, l) => (l.standardCurrency ? money(l.standardCostMicros, l.standardCurrency) : '—'),
    },
    {
      title: 'Booked vendor cost',
      key: 'vendor',
      align: 'right',
      render: (_, l) => (l.vendorCurrency ? money(l.bookedVendorCostMicros, l.vendorCurrency) : '—'),
    },
    { title: 'Adapter estimate', key: 'adapter', align: 'right', render: (_, l) => money(l.adapterCostMicrosUsd, 'USD') },
  ];

  return (
    <section style={{ marginBottom: 28 }} data-testid="capability-costs">
      <Title level={5} style={{ margin: 0 }}>
        Per-hit cost <InfoTip surface="admin" field="Per-hit cost" />
      </Title>
      <Paragraph type="secondary" style={{ margin: '4px 0 12px', fontSize: 13 }}>
        Billed capability calls in {month}, at the prices they were booked at. A retried or replayed call is booked once.
      </Paragraph>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Per-hit cost request failed" description={error} />}
      <Table<CostLine>
        rowKey={(l) => `${l.family}|${l.provider}|${l.standardCurrency}|${l.vendorCurrency}`}
        columns={columns}
        dataSource={costs?.lines ?? []}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No billed capability calls this month.' }}
      />
    </section>
  );
}

// ---- vendor statements ---------------------------------------------------------------------------------

function Differences({ provider }: { provider: ProviderReconciliation }) {
  const currency = provider.statement?.currency ?? null;
  const show = (unit: string, n: number) =>
    unit === 'amountMicros' ? `${currency ?? ''} ${fromMicros(n)}`.trim() : n.toLocaleString();
  return (
    <div style={{ fontSize: 12.5 }} data-testid={`statement-differences-${provider.provider}`}>
      <div style={{ marginBottom: 6 }}>
        <Text type="secondary">{provider.note}</Text>
      </div>
      {provider.differences.map((d) => (
        <div key={`${d.unit}|${d.dimension}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {UNIT[d.unit] ?? d.unit} <span className="ol-mono" style={{ fontSize: 12 }}>{d.dimension}</span>: statement{' '}
          {show(d.unit, d.statement)}, meter {show(d.unit, d.metered)} (
          {d.unit === 'amountMicros' ? (d.difference > 0 ? '+' : '') + fromMicros(d.difference) : signed(d.difference)})
        </div>
      ))}
      {provider.statement && (
        <div style={{ marginTop: 6 }}>
          <Text type="secondary">
            Statement {provider.statement.reference ?? provider.statement.statementId}, {provider.statement.format}, imported{' '}
            {formatAbsolute(provider.statement.importedAt)}
          </Text>
        </div>
      )}
    </div>
  );
}

function StatementsSection({ month, canImport }: { month: string; canImport: boolean }) {
  const { message } = App.useApp();
  const [view, setView] = useState<StatementReconciliation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [reference, setReference] = useState('');
  const [format, setFormat] = useState<'CSV' | 'JSON'>('CSV');
  const [content, setContent] = useState('');
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    if (!validMonth(month)) return;
    try {
      setView(await getStatementReconciliation(month));
      setError(null);
    } catch (e) {
      setError(normalizeApiError(e).message);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  const readFile = (file: File | undefined) => {
    if (!file) return;
    if (file.name.toLowerCase().endsWith('.json')) setFormat('JSON');
    if (file.name.toLowerCase().endsWith('.csv')) setFormat('CSV');
    void file.text().then(setContent);
  };

  const submit = async () => {
    setImporting(true);
    try {
      const imported = await importStatement({
        provider: provider.trim(),
        month,
        currency: currency.trim().toUpperCase(),
        reference: reference.trim() || undefined,
        format,
        content,
      });
      message.success(
        imported.alreadyImported
          ? 'This statement was already imported; nothing was added'
          : `Imported ${imported.lines.length} line(s) for ${imported.provider}`,
      );
      setContent('');
      await load();
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setImporting(false);
    }
  };

  const columns: ColumnsType<ProviderReconciliation> = [
    { title: 'Provider', dataIndex: 'provider', key: 'provider', render: (v: string) => <span className="ol-mono">{v}</span> },
    {
      title: 'Calls (statement / meter)',
      key: 'calls',
      render: (_, p) => {
        const stated = p.lines.reduce((n, l) => n + (l.statementCalls ?? 0), 0);
        const metered = p.lines.reduce((n, l) => n + l.meteredCalls, 0);
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {p.statement ? stated.toLocaleString() : '—'} / {metered.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: 'Result',
      key: 'result',
      width: 200,
      render: (_, p) => (
        <Tag color={RESULT[p.result].color} data-testid={`statement-result-${p.provider}`}>
          {RESULT[p.result].label}
        </Tag>
      ),
    },
  ];

  return (
    <section data-testid="vendor-statements">
      <Title level={5} style={{ margin: 0 }}>
        Vendor statements <InfoTip surface="admin" field="Vendor statement" />
      </Title>
      <Paragraph type="secondary" style={{ margin: '4px 0 12px', fontSize: 13 }}>
        A vendor&apos;s bill for one provider and {month}, compared with the meter per family. No vendor feed is connected:
        a statement is the file imported here.
      </Paragraph>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Statement request failed" description={error} />}
      <Table<ProviderReconciliation>
        rowKey="provider"
        columns={columns}
        dataSource={view?.providers ?? []}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No metered calls and no statements this month.' }}
        expandable={{
          rowExpandable: (p) => p.result !== 'NO_STATEMENT' || p.lines.length > 0,
          expandedRowRender: (p) => <Differences provider={p} />,
        }}
      />
      {canImport && (
        <div style={{ marginTop: 12 }} data-testid="statement-form">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
            <label style={{ fontSize: 11.5 }}>
              <div>Provider</div>
              <Input value={provider} onChange={(e) => setProvider(e.target.value)} style={{ width: 190 }} data-testid="statement-provider" />
            </label>
            <label style={{ fontSize: 11.5 }}>
              <div>Currency</div>
              <Input value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: 70 }} data-testid="statement-currency" />
            </label>
            <label style={{ fontSize: 11.5 }}>
              <div>Reference</div>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} style={{ width: 170 }} placeholder="the vendor's invoice no." />
            </label>
            <label style={{ fontSize: 11.5 }}>
              <div>Format</div>
              <Select
                value={format}
                onChange={setFormat}
                options={[
                  { value: 'CSV', label: 'CSV' },
                  { value: 'JSON', label: 'JSON' },
                ]}
                style={{ width: 90 }}
              />
            </label>
            <label style={{ fontSize: 11.5 }}>
              <div>File</div>
              <input
                type="file"
                accept=".csv,.json,text/csv,application/json"
                onChange={(e) => readFile(e.target.files?.[0])}
                data-testid="statement-file"
              />
            </label>
          </div>
          <Input.TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            placeholder={'family,calls,vendor_units,amount\nidentity-verification.v1,1200,1200,4800.00'}
            className="ol-mono"
            data-testid="statement-content"
          />
          <Button
            type="primary"
            style={{ marginTop: 8 }}
            onClick={() => void submit()}
            loading={importing}
            disabled={!provider.trim() || !content.trim()}
            data-testid="statement-import"
          >
            Import statement for {month}
          </Button>
        </div>
      )}
    </section>
  );
}

/** API-26: the price book, per-hit cost and vendor statements, under Admin > Metering. */
export function CapabilityBilling() {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const canWritePrices = roles.includes('hub_admin');
  const canImport = roles.some((r) => r === 'hub_admin' || r === 'hub_operator');
  const [month, setMonth] = useState(thisMonth());

  return (
    <div style={{ margin: '8px 0 28px' }} data-testid="capability-billing">
      <PriceBookSection canWrite={canWritePrices} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
          <div style={{ marginBottom: 3 }}>Cost and statement month</div>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Cost and statement month"
            data-testid="billing-month"
            style={{ width: 160 }}
          />
        </label>
      </div>
      <CostSection month={month} />
      <StatementsSection month={month} canImport={canImport} />
    </div>
  );
}
